import { EMPTY, Observable, of } from 'rxjs';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { ChatFormOperation, IChatForm } from '@nimbus-global/features/chat-form/models/chat-form.model';
import {
  IChatFormFieldAction,
  IChatFormFieldValidation,
  IChatFormResolver,
  IChatFormValueUpdate,
} from '@nimbus-global/features/chat-form/models/chat-form-resolver.model';
import { IOptionItem } from '@nimbus-library/features/shared/models/interfaces/i-option-item';
import { IChatFormEntityHandler } from './form-entity-handlers/i-chat-form-entity-handler';

export class CompanionChatFormService implements IChatFormResolver {
  private readonly handlersByEntity: Map<string, IChatFormEntityHandler>;

  public constructor(handlers: IChatFormEntityHandler[]) {
    this.handlersByEntity = new Map<string, IChatFormEntityHandler>(handlers.map((handler) => [handler.entity, handler]));
  }

  public resolveReferenceOptions = (
    referenceType: string,
    form: IChatForm,
    currentValues?: Record<string, unknown>,
  ): Observable<IOptionItem[]> =>
    this.handlersByEntity.get(form.entity)?.resolveReferenceOptions(referenceType, form, currentValues) ?? of([]);

  public resolveFieldTitle = (entity: string, field: string): TranslationKeys =>
    this.handlersByEntity.get(entity)?.resolveFieldTitle(field) ?? TranslationKeys.GeneralName;

  public resolveTitle = (entity: string, operation: ChatFormOperation): TranslationKeys | null => {
    switch (operation) {
      case ChatFormOperation.Confirm:
        return TranslationKeys.CompanionChatApprovalTitle;
      default:
        return this.handlersByEntity.get(entity)?.resolveFormTitle?.(operation) ?? null;
    }
  };

  public resolveDescription = (entity: string, operation: ChatFormOperation): TranslationKeys | null => {
    switch (operation) {
      case ChatFormOperation.Confirm:
        return TranslationKeys.CompanionChatApprovalDescription;
      default:
        return this.handlersByEntity.get(entity)?.formDescription ?? null;
    }
  };

  public resolveFieldAction = (entity: string, field: string, currentValues?: Record<string, unknown>): IChatFormFieldAction | null =>
    this.handlersByEntity.get(entity)?.resolveFieldAction?.(field, currentValues) ?? null;

  public resolveFieldMaxLength = (entity: string, field: string): number | null =>
    this.handlersByEntity.get(entity)?.resolveFieldMaxLength?.(field) ?? null;

  public resolveFieldValidation = (entity: string, field: string): IChatFormFieldValidation | null =>
    this.handlersByEntity.get(entity)?.resolveFieldValidation?.(field) ?? null;

  public observeFormValueUpdates = (entity: string): Observable<IChatFormValueUpdate> =>
    this.handlersByEntity.get(entity)?.formValueUpdate$ ?? EMPTY;

  public hasHandler(entity: string): boolean {
    return this.handlersByEntity.has(entity);
  }
}
